import { createAsyncThunk } from '@reduxjs/toolkit';
import { getBridgeMessager } from '@src/utils/BridgeMessager';
import { envConfig } from '@src/utils/envConfig';
import type {
  CustomerBalanceTransaction,
  DataResponse,
  RestDepositRequest,
  RestWithdrawRequest,
  WLAppExtraArgument,
  WLRootState,
} from '@talos/kyoko';
import {
  isCustomerBalanceTransactionTerminal,
  logger,
  Post,
  resetSwapForm,
  setSwapRequestStep,
  setSwapStep,
  setSwapTakingLongerThanExpected,
  setSwapTransaction,
  setSwapTransactionID,
  wait,
} from '@talos/kyoko';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
export const createBalanceTransactionThunk = createAsyncThunk<
  CustomerBalanceTransaction | undefined,
  void,
  {
    state: WLRootState;
    extra: WLAppExtraArgument | undefined;
  }
>('swap/createBalanceTransactionThunk', async (_arg, { dispatch, rejectWithValue, getState, extra }) => {
  const state = getState();
  const clReqId = uuid();

  let request: RestDepositRequest | RestWithdrawRequest | undefined;
  const currency = state.swap.form.fromCurrency.value?.Symbol ?? state.swap.referenceData.homeCurrency?.Symbol;
  if (!currency) {
    logger.error(new Error('No currency selected'));
    return rejectWithValue('Unable to perform this action (ERR1)');
  }
  if (!extra) {
    logger.error(new Error('No extra available on the store'));
    return rejectWithValue('Unable to perform this action (ERR2)');
  }

  if (state.swap.mode === 'withdraw') {
    request = {
      ClReqID: clReqId,
      TransactionType: 'Withdrawal',
      Currency: currency,
      Quantity: state.swap.form.fromQty.value ?? '',
      MarketAccount: state.swap.form.marketAccount.value?.SourceAccountID ?? '',
    } satisfies RestWithdrawRequest;
  } else if (state.swap.mode === 'topup') {
    request = {
      ClReqID: clReqId,
      TransactionType: 'Deposit',
      Currency: currency,
      Quantity: state.swap.form.fromQty.value ?? '',
      MarketAccount: state.swap.form.marketAccount.value?.SourceAccountID ?? '',
      TxHashes: [{ TxHash: uuid() }],
    } satisfies RestDepositRequest;
  }

  if (!request) {
    logger.error(new Error('The mode is not valid'), {
      extra: {
        mode: state.swap.mode,
      },
    });
    return rejectWithValue('Unable to perform this action (ERR3)');
  }

  dispatch(setSwapStep('sendingEntity'));
  try {
    // first we expect an initial response
    const endpoint =
      state.swap.mode === 'withdraw'
        ? '/transactions/withdrawal'
        : state.swap.mode === 'topup'
          ? '/transactions/deposit'
          : undefined;

    if (!endpoint) {
      logger.error(new Error('The mode is not valid'), {
        extra: {
          mode: state.swap.mode,
        },
      });
      dispatch(setSwapRequestStep('resting'));
      dispatch(setSwapStep('idle'));
      return rejectWithValue('Unable to perform this action (ERR4)');
    }

    const response = await Post<DataResponse<CustomerBalanceTransaction>>(
      envConfig.VITE_API_ENDPOINT,
      endpoint,
      request,
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (response === null) {
      dispatch(setSwapStep('idle'));
      return;
    }

    const customerBalanceTransactionRESTResponse = response.data[0];
    dispatch(setSwapTransactionID(customerBalanceTransactionRESTResponse.TransactionID));

    if (state.swap.mode === 'topup') {
      if (!getBridgeMessager().checkIfWithinSuperApp()) {
        // if we are running outside the super app, there is no cashier to open and no one will handle the transfer.
        // this means the transfer needs to be approved manually in /dealer/transfers.
        dispatch(setSwapRequestStep('waiting-for-confirmation'));
      } else {
        if (!customerBalanceTransactionRESTResponse.RoutingInfo?.ExternalTxnURL) {
          dispatch(setSwapRequestStep('resting'));
          dispatch(setSwapStep('idle'));
          return rejectWithValue('Unable to perform this action (ERR5)');
        }

        dispatch(setSwapRequestStep('opening-cashier'));
        const result = await getBridgeMessager().sendMessage({
          id: uuid(),
          type: 'init_cashier',
          payload: { paymentUrl: customerBalanceTransactionRESTResponse.RoutingInfo.ExternalTxnURL },
        });
        if (result.payload?.result === 'error') {
          dispatch(setSwapRequestStep('resting'));
          dispatch(setSwapStep('idle'));
          return rejectWithValue(result.payload.text);
        }
        if (result.payload.result === 'dismissed') {
          // user dismissed the cashier
          dispatch(setSwapRequestStep('resting'));
          dispatch(setSwapStep('idle'));
          return;
        }
        // Only set to 'waiting-for-confirmation' if cashier was successfully opened
        dispatch(setSwapRequestStep('waiting-for-confirmation'));
      }
    }

    // wait for 1 second after the cashier is closed, then start checking for the transaction
    await wait(1_000);

    // if we don't receive a terminal customer balance transaction after 5 seconds, set taking longer than expected to true
    const fiveSecondTimeoutId = setTimeout(() => {
      dispatch(setSwapTakingLongerThanExpected(true));
    }, 5_000);

    const terminalCustomerBalanceTransactionState = await firstValueFrom(
      extra.sessionTransactionsObs.pipe(
        filter(transaction => transaction.ClReqID === clReqId),
        filter(transaction => isCustomerBalanceTransactionTerminal(transaction.Status)),
        timeout({
          each: 60_000,
          with: () => {
            clearTimeout(fiveSecondTimeoutId);
            throw new Error('Transaction confirmation timed out');
          },
        })
      )
    );
    clearTimeout(fiveSecondTimeoutId);
    dispatch(setSwapTakingLongerThanExpected(false));
    dispatch(setSwapRequestStep('resting'));
    dispatch(setSwapStep('sentEntity'));
    dispatch(resetSwapForm({ keepFromQty: true }));
    dispatch(setSwapTransaction(terminalCustomerBalanceTransactionState));
    return terminalCustomerBalanceTransactionState;
  } catch (error) {
    dispatch(resetSwapForm({ keepFromQty: true }));
    return rejectWithValue(error instanceof Error ? error.message : 'Failed to create balance transaction');
  }
});
